import React, { useState, useEffect } from 'react'
import {
  EditChefMenuItemReq,
  ChefMenuItem,
  CreateChefMenuItemReq,
  MenuItemMod,
  MenuItemModGroup,
  MenuItemModVerb,
  verbConfigs,
} from '../../../../types/groupOrder/menuItemMods'
import CurrencyInput from '@components/common/form/CurrencyInput'
import { pMenuItemModsToChanges } from '@presenters/api/menuItemMods'
import { cloneDeep } from 'lodash'
import MenuItemModGroupPreview from './MenuItemModGroupPreview'

interface EditMenuItemModsProps {
  chefId: string
  chefMenuItemId: string
  chefMenuItem: ChefMenuItem
  createChefMenuItem: (
    req: CreateChefMenuItemReq,
  ) => Promise<ChefMenuItem | null>
  editChefMenuItem: (req: EditChefMenuItemReq) => Promise<ChefMenuItem | null>
  displayWarningMessage: (message: string) => void
  onSave: (chefMenuItem: ChefMenuItem) => void
  calculateClientPrice: (price: number, markup: number) => number
  calculateRestaurantPrice: (price: number, markup: number) => number
  calculateRestaurantPriceFromRetail: (price: number) => number
}

const formatModVerb = (verb: MenuItemModVerb): string => {
  return verb.charAt(0).toUpperCase() + verb.slice(1).toLowerCase()
}

const newId = () => {
  return 'new-' + Math.random().toString(36).substring(2, 15)
}

const isNewId = (id: string): boolean => {
  return id.startsWith('new-')
}

const buildNewModGroup = (maxPosition: number): MenuItemModGroup => {
  return {
    id: newId(),
    name: 'New Mod Group',
    verb: MenuItemModVerb.Add,
    isSingleOpt: false,
    isRequired: false,
    position: maxPosition + 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
    mods: [],
  }
}

const buildNewMod = (maxPosition: number): MenuItemMod => {
  return {
    id: newId(),
    name: 'New Mod',
    position: maxPosition + 1,
    clientPrice: 0,
    restaurantPrice: 0,
    retailPrice: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
  }
}

const EditMenuItemMods: React.FC<EditMenuItemModsProps> = ({
  chefId,
  chefMenuItemId,
  chefMenuItem,
  createChefMenuItem,
  displayWarningMessage,
  editChefMenuItem,
  onSave,
  calculateClientPrice,
  calculateRestaurantPrice,
  calculateRestaurantPriceFromRetail,
}) => {
  const [openGroupIds, setOpenGroupIds] = useState<string[]>([])
  const [modsActive, setModsActive] = useState(false)
  const [prevModGroups, setPrevModGroups] = useState<MenuItemModGroup[]>([])
  const [nextModGroups, setNextModGroups] = useState<MenuItemModGroup[]>([])
  const [markupPercentage, setMarkupPercentage] = useState(0.4)
  const [showMarkupPopupFor, setShowMarkupPopupFor] = useState<{
    groupId: string
    modId: string
  } | null>(null)
  const [jsonInput, setJsonInput] = useState('')
  const [isImporting, setIsImporting] = useState(false)
  const hasNoPrevModGroups = prevModGroups.length === 0

  useEffect(() => {
    if (chefMenuItem) {
      const modGroups = cloneDeep(chefMenuItem.availableModGroups)
      setNextModGroups(modGroups)
      setPrevModGroups(cloneDeep(chefMenuItem.availableModGroups))
      setOpenGroupIds(modGroups.map((g: MenuItemModGroup) => g.id)) // Default all groups to open
      setModsActive(chefMenuItem.modsActive)
    }
  }, [chefMenuItem])

  const toggleGroup = (groupId: string) => {
    setOpenGroupIds((prevOpenGroupIds) =>
      prevOpenGroupIds.includes(groupId)
        ? prevOpenGroupIds.filter((id) => id !== groupId)
        : [...prevOpenGroupIds, groupId],
    )
  }

  const handleGroupChange = (
    groupId: string,
    field: keyof MenuItemModGroup,
    value: any,
  ) => {
    setNextModGroups((currentGroups) =>
      currentGroups.map((group) => {
        if (group.id === groupId) {
          if (field === 'isRequired') {
            displayWarningMessage('Cannot change "Is Required" for this verb')

            return group
          }
          if (field === 'isSingleOpt') {
            const verbConfig = verbConfigs[group.verb]
            if (verbConfig.isSingleOpt != null) {
              displayWarningMessage(
                'Cannot change "Is Single Opt" for this verb',
              )

              return group
            }
          }

          return {
            ...group,
            [field]: value,
            updatedAt: new Date().toISOString(), // Update timestamp
          }
        }

        return group
      }),
    )
  }

  const handleGroupChangeVerb = (
    groupIndex: number,
    value: MenuItemModVerb,
  ) => {
    const updatedGroups = [...nextModGroups]
    const verbConfig = verbConfigs[value]
    if (!verbConfig) {
      return
    }
    updatedGroups[groupIndex] = {
      ...updatedGroups[groupIndex],
      verb: value,
      isRequired: verbConfig.isRequired,
      isSingleOpt: verbConfig.isSingleOpt || false,
      updatedAt: new Date().toISOString(),
    }
    setNextModGroups(updatedGroups)
  }

  const handleGroupPositionChange = (groupId: string, newPosition: number) => {
    setNextModGroups((currentGroups) => {
      const groupToMove = currentGroups.find((g) => g.id === groupId)

      if (
        !groupToMove ||
        newPosition < 1 ||
        newPosition > currentGroups.length
      ) {
        return currentGroups
      }

      const remainingGroups = currentGroups.filter((g) => g.id !== groupId)
      remainingGroups.splice(newPosition - 1, 0, groupToMove)

      let nextPosition = 0

      return remainingGroups.map((group) => {
        if (group.deletedAt) {
          return group
        }
        nextPosition++

        return { ...group, position: nextPosition }
      })
    })
  }

  const handleAddNewGroup = () => {
    const currentMaxGroupPosition = nextModGroups
      .filter((group) => !group.deletedAt)
      .reduce((max, group) => Math.max(max, group.position || 0), 0)
    const newGroup = buildNewModGroup(currentMaxGroupPosition)
    setNextModGroups([...nextModGroups, newGroup])
    // Optionally, open the new group for immediate editing
    setOpenGroupIds((prev) => [...prev, newGroup.id])
  }

  const handleDeleteGroup = (groupIndex: number) => {
    const groupToDelete = nextModGroups[groupIndex]
    if (!groupToDelete) {
      return
    }

    let updatedGroups: MenuItemModGroup[]
    if (groupToDelete.deletedAt) {
      // Undelete: clear deletedAt timestamp
      updatedGroups = nextModGroups.map((group, index) =>
        index === groupIndex
          ? { ...group, deletedAt: null, updatedAt: new Date().toISOString() }
          : group,
      )
    } else if (isNewId(groupToDelete.id)) {
      // Delete new group: remove from list
      updatedGroups = nextModGroups.filter((_, index) => index !== groupIndex)
    } else {
      // Mark existing group for deletion: set deletedAt timestamp
      updatedGroups = nextModGroups.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              deletedAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }
          : group,
      )
    }
    let nextPosition = 0
    updatedGroups = updatedGroups.map((group) => {
      if (group.deletedAt) {
        return group
      }
      nextPosition++

      return { ...group, position: nextPosition }
    })
    setNextModGroups(updatedGroups)
    // If the deleted group was open, close it or handle accordingly
    if (openGroupIds.includes(groupToDelete.id)) {
      setOpenGroupIds((prev) => prev.filter((id) => id !== groupToDelete.id))
    }
  }

  const handleAddNewMod = (groupIndex: number) => {
    const group = nextModGroups[groupIndex]
    if (!group) {
      return
    }

    const currentMaxModPosition = (group.mods || [])
      .filter((mod) => !mod.deletedAt)
      .reduce((max, mod) => Math.max(max, mod.position || 0), 0)
    const newMod = buildNewMod(currentMaxModPosition)

    const updatedGroups = [...nextModGroups]
    const updatedMods = [...(updatedGroups[groupIndex].mods || []), newMod]

    updatedGroups[groupIndex] = {
      ...updatedGroups[groupIndex],
      mods: updatedMods,
      updatedAt: new Date().toISOString(), // Also update group's timestamp
    }
    setNextModGroups(updatedGroups)
  }

  const handleDeleteMod = (groupIndex: number, modIndex: number) => {
    const group = nextModGroups[groupIndex]
    if (!group || !group.mods) {
      return
    }

    const modToDelete = group.mods[modIndex]
    if (!modToDelete) {
      return
    }

    let updatedMods: MenuItemMod[]
    if (modToDelete.deletedAt) {
      // Undelete: clear deletedAt timestamp
      updatedMods = group.mods.map((mod, index) =>
        index === modIndex
          ? { ...mod, deletedAt: null, updatedAt: new Date().toISOString() }
          : mod,
      )
    } else if (isNewId(modToDelete.id)) {
      // Delete new mod: remove from list
      updatedMods = group.mods.filter((_, index) => index !== modIndex)
    } else {
      // Mark existing mod for deletion: set deletedAt timestamp
      updatedMods = group.mods.map((mod, index) =>
        index === modIndex
          ? {
              ...mod,
              deletedAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }
          : mod,
      )
    }

    let nextPosition = 0
    updatedMods = updatedMods.map((mod) => {
      if (mod.deletedAt) {
        return mod
      }
      nextPosition++

      return { ...mod, position: nextPosition }
    })

    const updatedGroups = [...nextModGroups]
    updatedGroups[groupIndex] = {
      ...updatedGroups[groupIndex],
      mods: updatedMods,
      updatedAt: new Date().toISOString(), // Also update group's timestamp
    }
    setNextModGroups(updatedGroups)
  }

  const handleModChange = (
    groupId: string,
    modId: string,
    field: keyof MenuItemMod,
    value: any,
  ) => {
    setNextModGroups((currentGroups) =>
      currentGroups.map((group) => {
        if (group.id === groupId) {
          const updatedMods = (group.mods || []).map((mod) => {
            if (mod.id === modId) {
              return {
                ...mod,
                [field]: value,
                updatedAt: new Date().toISOString(),
              }
            }

            return mod
          })

          return {
            ...group,
            mods: updatedMods,
            updatedAt: new Date().toISOString(),
          }
        }

        return group
      }),
    )
  }

  const handleModPositionChange = (
    groupId: string,
    modId: string,
    newPosition: number,
  ) => {
    setNextModGroups((currentGroups) =>
      currentGroups.map((group) => {
        if (group.id === groupId) {
          const mods = group.mods || []
          const modToMove = mods.find((m) => m.id === modId)

          if (!modToMove || newPosition < 1 || newPosition > mods.length) {
            return group
          }

          const remainingMods = mods.filter((m) => m.id !== modId)
          remainingMods.splice(newPosition - 1, 0, modToMove)

          let nextPosition = 0
          const updatedMods = remainingMods.map((mod) => {
            if (mod.deletedAt) {
              return mod
            }
            nextPosition++

            return { ...mod, position: nextPosition }
          })

          return { ...group, mods: updatedMods }
        }

        return group
      }),
    )
  }

  const handleSave = async () => {
    if (modsActive && nextModGroups.length === 0) {
      displayWarningMessage(
        'Cannot activate mods when no modification groups are defined.',
      )

      return
    }

    const { editModGroups, createModGroups } = pMenuItemModsToChanges(
      prevModGroups,
      nextModGroups,
    )

    if (
      editModGroups.length === 0 &&
      createModGroups.length === 0 &&
      chefMenuItem.modsActive === modsActive
    ) {
      return
    }

    let nextChefMenuItem: ChefMenuItem | null = null
    if (prevModGroups.length > 0) {
      const req: EditChefMenuItemReq = {
        id: chefMenuItemId,
        modsActive,
        createAvailableModGroups: createModGroups,
        editAvailableModGroups: editModGroups,
      }
      nextChefMenuItem = await editChefMenuItem(req)
    } else {
      const req: CreateChefMenuItemReq = {
        chefId,
        chefMenuItemId,
        modsActive,
        availableModGroups: createModGroups,
      }
      nextChefMenuItem = await createChefMenuItem(req)
    }
    if (nextChefMenuItem) {
      setPrevModGroups(cloneDeep(nextChefMenuItem.availableModGroups))
      setNextModGroups(cloneDeep(nextChefMenuItem.availableModGroups))
      onSave(nextChefMenuItem)
    }
  }

  const handleExport = () => {
    const hadDeletedItems = nextModGroups.some(
      (g) => g.deletedAt || (g.mods && g.mods.some((m) => m.deletedAt)),
    )

    const cleanModGroups = nextModGroups
      .filter((group) => !group.deletedAt)
      .map((group) => ({
        ...group,
        mods: (group.mods || []).filter((mod) => !mod.deletedAt),
      }))

    const jsonToCopy = JSON.stringify(cleanModGroups, null, 2)

    navigator.clipboard
      .writeText(jsonToCopy)
      .then(() => {
        if (hadDeletedItems) {
          displayWarningMessage(
            'Deleted mod groups and items were not included in the export. Copied to clipboard.',
          )
        } else {
          displayWarningMessage('Successfully copied to clipboard!')
        }
      })
      .catch((err) => {
        displayWarningMessage(`Failed to copy to clipboard: ${err}`)
      })
  }

  const handleImport = () => {
    if (!jsonInput) {
      displayWarningMessage('Text area is empty. Nothing to import.')

      return
    }
    try {
      const importedModGroups = JSON.parse(jsonInput) as MenuItemModGroup[]

      if (!Array.isArray(importedModGroups)) {
        displayWarningMessage('Invalid JSON format: not an array.')

        return
      }

      // Process imported groups to treat them as new and avoid ID conflicts
      const newModGroups = importedModGroups.map((group) => {
        // Simple validation for a group
        if (!group.name || !group.verb) {
          throw new Error('Imported group is missing name or verb.')
        }
        const newGroup: MenuItemModGroup = {
          ...group,
          id: newId(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deletedAt: null,
          mods: (group.mods || []).map((mod) => {
            if (!mod.name) {
              throw new Error('Imported mod is missing name.')
            }

            return {
              ...mod,
              id: newId(),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              deletedAt: null,
            }
          }),
        }

        return newGroup
      })

      // Append to existing groups and re-order positions
      setNextModGroups((currentGroups: MenuItemModGroup[]) => {
        let nextModGroups: MenuItemModGroup[] = []
        if (hasNoPrevModGroups) {
          nextModGroups = [...currentGroups, ...newModGroups]
        } else {
          const ts = new Date().toISOString()
          const deleted = currentGroups.map((group) => {
            if (group.deletedAt) {
              return group
            } else {
              return {
                ...group,
                deletedAt: ts,
                updatedAt: ts,
              }
            }
          })
          nextModGroups = [...deleted, ...newModGroups]
        }
        let nextPosition = 0

        return nextModGroups.map((group) => {
          if (group.deletedAt) {
            return group
          }
          nextPosition++

          return { ...group, position: nextPosition }
        })
      })

      displayWarningMessage('Successfully imported and added new mod groups!')
      setJsonInput('') // Clear textarea after import
      setIsImporting(false)
    } catch (error: any) {
      displayWarningMessage(
        `Import failed: ${error.message || 'Invalid JSON format.'}`,
      )
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-gray-600 my-6 p-4 shadow">
        Item modifications are used to allow customers to customize their meals
        with optional or required options. Modifications are currently only
        supported for group orders.
      </p>

      <div className="flex items-center space-x-2 p-4">
        <input
          type="checkbox"
          id="modsActive"
          checked={modsActive}
          onChange={(e) => setModsActive(e.target.checked)}
          className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="modsActive" className="font-medium text-gray-800">
          Modifications are active for this item
        </label>
      </div>

      {!nextModGroups ||
        (nextModGroups.length === 0 && (
          <p className="text-gray-600 my-6">
            No modification groups available for this item.
          </p>
        ))}

      {nextModGroups.map((group, groupIndex) => {
        const isGroupDeleted = !!group.deletedAt
        const verbConfig = verbConfigs[group.verb]

        return (
          <div
            key={group.id || groupIndex}
            className={`border border-gray-400 rounded shadow-sm ${
              isGroupDeleted ? 'bg-red-100 opacity-70' : ''
            }`}
          >
            <div
              onClick={() => !isGroupDeleted && toggleGroup(group.id)}
              className={`flex justify-between items-center font-bold p-3 ${
                isGroupDeleted
                  ? 'cursor-not-allowed bg-red-300 text-white'
                  : 'cursor-pointer bg-gray-400 hover:bg-gray-500'
              }`}
            >
              <span className="font-semibold text-lg">
                {formatModVerb(group.verb)} {group.name}
                {isGroupDeleted && (
                  <span className="font-normal italic">
                    {' '}
                    (Marked for Deletion)
                  </span>
                )}
              </span>
              <div className="flex items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation() // Prevent toggleGroup when clicking delete/undelete
                    handleDeleteGroup(groupIndex)
                  }}
                  className={`mr-4 px-2 py-1 text-white text-xs rounded focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                    isGroupDeleted
                      ? 'bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500'
                      : 'bg-red-500 hover:bg-red-600 focus:ring-red-500'
                  }`}
                >
                  {isGroupDeleted ? 'Undo Delete' : 'Delete Group'}
                </button>
                {!isGroupDeleted && (
                  <span>{openGroupIds.includes(group.id) ? '▲' : '▼'}</span>
                )}
              </div>
            </div>
            {openGroupIds.includes(group.id) && !isGroupDeleted && (
              <div className="flex">
                <div className="w-2/3">
                  <div className="p-4 text-sm text-gray-700 bg-gray-300 h-full">
                    {/* Primary Group Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 mb-3">
                      <div>
                        <strong>Group Name:</strong>
                        <input
                          type="text"
                          value={group.name}
                          onChange={(e) =>
                            handleGroupChange(group.id, 'name', e.target.value)
                          }
                          className="ml-2 p-1 border border-gray-300 rounded w-full"
                          disabled={isGroupDeleted}
                        />
                      </div>
                      <div>
                        <strong>Verb:</strong>
                        <select
                          value={group.verb}
                          onChange={(e) =>
                            handleGroupChangeVerb(
                              groupIndex,
                              e.target.value as MenuItemModVerb,
                            )
                          }
                          className="ml-2 p-1 border border-gray-300 rounded w-full"
                          disabled={isGroupDeleted}
                        >
                          {Object.values(MenuItemModVerb).map((verbValue) => (
                            <option key={verbValue} value={verbValue}>
                              {formatModVerb(verbValue)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <strong>Position:</strong>
                        <select
                          value={group.position}
                          onChange={(e) =>
                            handleGroupPositionChange(
                              group.id,
                              parseInt(e.target.value, 10),
                            )
                          }
                          className="ml-2 p-1 border border-gray-300 rounded w-full"
                          disabled={isGroupDeleted}
                        >
                          {Array.from(
                            { length: nextModGroups.length },
                            (_, i) => i + 1,
                          ).map((pos) => (
                            <option key={pos} value={pos}>
                              {pos}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <strong className="mr-2">Required:</strong>
                        <input
                          type="checkbox"
                          checked={group.isRequired}
                          onChange={(e) =>
                            handleGroupChange(
                              group.id,
                              'isRequired',
                              e.target.checked,
                            )
                          }
                          className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          disabled={true}
                        />
                      </div>
                      <div>
                        <strong className="mr-2">Single Option:</strong>
                        <input
                          type="checkbox"
                          checked={group.isSingleOpt}
                          onChange={(e) =>
                            handleGroupChange(
                              group.id,
                              'isSingleOpt',
                              e.target.checked,
                            )
                          }
                          className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          disabled={
                            isGroupDeleted || verbConfig.isSingleOpt != null
                          }
                        />
                      </div>
                    </div>

                    {/* Timestamp Details */}
                    {(group.createdAt ||
                      group.updatedAt ||
                      group.deletedAt) && (
                      <div className="mt-3 pt-3 border-t border-gray-300 text-xs text-gray-600">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1">
                          {group.createdAt && (
                            <div>
                              <strong>Created:</strong>{' '}
                              {new Date(group.createdAt).toLocaleString()}
                            </div>
                          )}
                          {group.updatedAt && (
                            <div>
                              <strong>Updated:</strong>{' '}
                              {new Date(group.updatedAt).toLocaleString()}
                            </div>
                          )}
                          {group.deletedAt && (
                            <div className="text-red-600">
                              <strong>Deleted:</strong>{' '}
                              {new Date(group.deletedAt).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <h4 className="mt-4 mb-2 pt-3 border-t border-dashed border-gray-300 font-semibold text-base">
                      Available Options (
                      {group.mods?.filter((mod) => !mod.deletedAt).length || 0}
                      ):
                    </h4>
                    {group.mods && group.mods.length > 0 ? (
                      <ul className="list-none mt-2 bg-gray-100 rounded-lg p-4 space-y-3">
                        {group.mods.map((mod, modIndex) => {
                          const isModDeleted = !!mod.deletedAt

                          return (
                            <li
                              key={mod.id || modIndex}
                              className={`border-b border-slate-200 last:border-b-0 pb-3 last:pb-0 ${
                                isModDeleted ? 'bg-red-200' : ''
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <strong
                                  className={`block mb-1 font-medium text-gray-800 ${
                                    isModDeleted ? 'line-through' : ''
                                  }`}
                                >
                                  Option Name:
                                  <input
                                    type="text"
                                    value={mod.name}
                                    onChange={(e) =>
                                      handleModChange(
                                        group.id,
                                        mod.id,
                                        'name',
                                        e.target.value,
                                      )
                                    }
                                    className="ml-2 p-1 border border-gray-300 rounded w-auto"
                                    disabled={isGroupDeleted || isModDeleted}
                                  />
                                </strong>
                                {!isGroupDeleted && (
                                  <button
                                    onClick={() =>
                                      handleDeleteMod(groupIndex, modIndex)
                                    }
                                    className={`ml-2 px-2 py-0.5 text-white text-xs rounded focus:outline-none focus:ring-1 focus:ring-opacity-50 ${
                                      isModDeleted
                                        ? 'bg-yellow-400 hover:bg-yellow-500 focus:ring-yellow-400'
                                        : 'bg-red-400 hover:bg-red-500 focus:ring-red-400'
                                    }`}
                                  >
                                    {isModDeleted
                                      ? 'Undo Delete'
                                      : 'Delete Mod'}
                                  </button>
                                )}
                              </div>
                              {!isModDeleted && (
                                <div
                                  className={`pl-3 text-xs text-gray-600 space-y-1 ${
                                    isGroupDeleted
                                      ? 'pointer-events-none opacity-50'
                                      : ''
                                  }`}
                                >
                                  <div className="grid grid-cols-4 gap-x-2 items-start">
                                    <div className="flex flex-col">
                                      <strong className="mb-0.5">
                                        Position:
                                      </strong>
                                      <select
                                        value={mod.position}
                                        onChange={(e) =>
                                          handleModPositionChange(
                                            group.id,
                                            mod.id,
                                            parseInt(e.target.value, 10),
                                          )
                                        }
                                        className="p-1 border border-gray-300 rounded w-1/2"
                                        disabled={
                                          isGroupDeleted || isModDeleted
                                        }
                                      >
                                        {Array.from(
                                          {
                                            length: (group.mods || []).length,
                                          },
                                          (_, i) => i + 1,
                                        ).map((pos) => (
                                          <option key={pos} value={pos}>
                                            {pos}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="flex flex-col">
                                      <strong className="mb-0.5">
                                        Restaurant Price:
                                      </strong>
                                      <CurrencyInput
                                        value={mod.restaurantPrice}
                                        onChange={(newValue) =>
                                          handleModChange(
                                            group.id,
                                            mod.id,
                                            'restaurantPrice',
                                            newValue || 0,
                                          )
                                        }
                                        className="p-1 border border-gray-300 rounded w-full text-right"
                                        disabled={
                                          isGroupDeleted || isModDeleted
                                        }
                                      />
                                      <div className="flex flex-row justify-center text-center text-xs mt-1">
                                        <p className="font-bold mr-1">Via</p>
                                        <a
                                          className="text-blue-600 hover:underline cursor-pointer"
                                          onClick={() => {
                                            const newRestaurantPrice =
                                              calculateRestaurantPrice(
                                                mod.clientPrice,
                                                markupPercentage,
                                              )
                                            handleModChange(
                                              group.id,
                                              mod.id,
                                              'restaurantPrice',
                                              newRestaurantPrice,
                                            )
                                          }}
                                        >
                                          Client
                                        </a>
                                        <div className="mx-1">
                                          <p className="font-bold">/</p>
                                        </div>
                                        <a
                                          className="text-blue-600 hover:underline cursor-pointer"
                                          onClick={() => {
                                            const newRestaurantPrice =
                                              calculateRestaurantPriceFromRetail(
                                                mod.retailPrice,
                                              )
                                            handleModChange(
                                              group.id,
                                              mod.id,
                                              'restaurantPrice',
                                              newRestaurantPrice,
                                            )
                                          }}
                                        >
                                          Retail
                                        </a>
                                      </div>
                                    </div>
                                    <div className="flex flex-col">
                                      <strong className="mb-0.5">
                                        Client Price:
                                      </strong>
                                      <CurrencyInput
                                        value={mod.clientPrice}
                                        onChange={(newValue) =>
                                          handleModChange(
                                            group.id,
                                            mod.id,
                                            'clientPrice',
                                            newValue || 0,
                                          )
                                        }
                                        className="p-1 border border-gray-300 rounded w-full text-right"
                                        disabled={
                                          isGroupDeleted || isModDeleted
                                        }
                                      />
                                      <div className="flex flex-row justify-center text-center text-xs mt-1">
                                        <p className="font-bold mr-1">Via</p>
                                        <a
                                          className="text-blue-600 hover:underline cursor-pointer"
                                          onClick={() =>
                                            setShowMarkupPopupFor({
                                              groupId: group.id,
                                              modId: mod.id,
                                            })
                                          }
                                        >
                                          Restaurant
                                        </a>
                                        <dialog
                                          open={
                                            showMarkupPopupFor?.groupId ===
                                              group.id &&
                                            showMarkupPopupFor?.modId === mod.id
                                          }
                                          style={{
                                            zIndex: '5000',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            position: 'fixed',
                                          }}
                                          className="p-4 border rounded shadow-lg bg-white w-80"
                                        >
                                          <p className="font-semibold mb-2">
                                            Please select market markup
                                            percentage:
                                          </p>
                                          <form>
                                            {[0.15, 0.2, 0.3, 0.4, 0.5].map(
                                              (p) => (
                                                <div
                                                  className="radio my-1"
                                                  key={p}
                                                >
                                                  <label className="flex items-center">
                                                    <input
                                                      type="radio"
                                                      value={p}
                                                      checked={
                                                        markupPercentage === p
                                                      }
                                                      onChange={(e) =>
                                                        setMarkupPercentage(
                                                          Number(
                                                            e.target.value,
                                                          ),
                                                        )
                                                      }
                                                      className="mr-2"
                                                    />
                                                    {p * 100}%
                                                  </label>
                                                </div>
                                              ),
                                            )}
                                          </form>
                                          <div className="flex justify-between mt-4">
                                            <button
                                              onClick={() => {
                                                const newClientPrice =
                                                  calculateClientPrice(
                                                    mod.restaurantPrice,
                                                    markupPercentage,
                                                  )
                                                handleModChange(
                                                  group.id,
                                                  mod.id,
                                                  'clientPrice',
                                                  newClientPrice,
                                                )
                                                setShowMarkupPopupFor(null)
                                              }}
                                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                            >
                                              Save
                                            </button>
                                            <button
                                              onClick={() =>
                                                setShowMarkupPopupFor(null)
                                              }
                                              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </dialog>
                                      </div>
                                    </div>
                                    <div className="flex flex-col">
                                      <strong className="mb-0.5">
                                        Retail Price:
                                      </strong>
                                      <CurrencyInput
                                        value={mod.retailPrice}
                                        onChange={(newValue) =>
                                          handleModChange(
                                            group.id,
                                            mod.id,
                                            'retailPrice',
                                            newValue || 0,
                                          )
                                        }
                                        className="p-1 border border-gray-300 rounded w-full text-right"
                                        disabled={
                                          isGroupDeleted || isModDeleted
                                        }
                                      />
                                    </div>
                                  </div>

                                  {/* Row 3: Timestamps */}
                                  {(mod.createdAt ||
                                    mod.updatedAt ||
                                    mod.deletedAt) &&
                                    !isModDeleted && (
                                      <div className="mt-1 pt-1 border-t border-slate-200">
                                        <div className="grid grid-cols-4 gap-x-2 gap-y-0.5">
                                          {mod.createdAt && (
                                            <div>
                                              Created:{' '}
                                              {new Date(
                                                mod.createdAt,
                                              ).toLocaleString()}
                                            </div>
                                          )}
                                          {mod.updatedAt && (
                                            <div>
                                              Updated:{' '}
                                              {new Date(
                                                mod.updatedAt,
                                              ).toLocaleString()}
                                            </div>
                                          )}
                                          {/* Display deletedAt for mods only if they are NOT soft deleted (it's for historical data if shown) */}
                                          {mod.deletedAt && !isModDeleted && (
                                            <div className="text-red-600">
                                              Deleted:{' '}
                                              {new Date(
                                                mod.deletedAt,
                                              ).toLocaleString()}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              )}
                              {/* If mod is deleted, but its group is not, we might want to show a simplified timestamp or specific message */}
                              {isModDeleted &&
                                !isGroupDeleted &&
                                mod.updatedAt && (
                                  <div className="pl-3 mt-1 text-xs text-gray-500">
                                    Marked for deletion on:{' '}
                                    {new Date(mod.updatedAt).toLocaleString()}
                                  </div>
                                )}
                            </li>
                          )
                        })}
                      </ul>
                    ) : (
                      <p className="text-gray-500 italic mt-2 pl-4">
                        No specific modifications in this group.
                      </p>
                    )}
                    <button
                      onClick={() => handleAddNewMod(groupIndex)}
                      className="mt-3 px-3 py-1.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      disabled={isGroupDeleted}
                    >
                      Add New Mod to this Group
                    </button>
                  </div>
                </div>
                <MenuItemModGroupPreview group={group} />
              </div>
            )}
          </div>
        )
      })}
      <div className="mt-6 flex items-center space-x-4">
        {nextModGroups.length > 0 && (
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
          >
            Save Changes
          </button>
        )}
        <button
          onClick={handleAddNewGroup}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
        >
          Add New Mod Group
        </button>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
        >
          Export to Clipboard
        </button>
      </div>
      <div className="mt-6 p-4 border-t border-gray-200">
        {!isImporting ? (
          <button
            onClick={() => setIsImporting(true)}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
          >
            Import from JSON
          </button>
        ) : (
          <div>
            <h3 className="text-lg font-semibold mb-2">
              Import Mod Groups from JSON
            </h3>
            {!hasNoPrevModGroups && (
              <p className="text-md font-semibold mb-2 text-red-600">
                Importing a new set of mod groups will mark the old mod groups
                for deletion.
              </p>
            )}
            <textarea
              className="w-full h-40 p-2 border border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Paste JSON here to import..."
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
            />
            <div className="mt-2 flex space-x-2">
              <button
                onClick={handleImport}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
              >
                Confirm Import
              </button>
              <button
                onClick={() => {
                  setIsImporting(false)
                  setJsonInput('')
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditMenuItemMods
